/* ==========================================================================
   Mirah — product.js
   <product-gallery>, <size-guide>, <product-tabs>, <product-recommendations>,
   <recently-viewed>, sticky add-to-cart.
   ========================================================================== */
(function () {
  'use strict';

  var d = document;
  var Theme = window.Theme || (window.Theme = {});
  var isMobile = function () { return window.matchMedia('(max-width: 767.98px)').matches; };
  var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------- Gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.main = this.querySelector('[data-gallery-main]');
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-thumb]'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('.pdp__dot'));
      this.scope = this.closest('[data-product-scope]') || d;
      var self = this;

      this.prev = this.querySelector('[data-gallery-prev]');
      this.next = this.querySelector('[data-gallery-next]');
      this.now = this.querySelector('[data-gallery-now]');
      this.bar = this.querySelector('[data-gallery-bar]');
      var init = function () {
        if (!window.Swiper || !self.main) return;
        self.swiper = new Swiper(self.main, {
          speed: 1100,
          grabCursor: true,
          resistanceRatio: 0.5,
          on: {
            slideChange: function (s) { self.setActive(s.activeIndex); self.films(s); },
            // The incoming photo settles from a slight zoom as it slides in.
            slideChangeTransitionStart: function (s) {
              if (!Theme.motionOn || !window.gsap) return;
              var img = s.slides[s.activeIndex] && s.slides[s.activeIndex].querySelector('img');
              if (img) gsap.fromTo(img, { scale: 1.14 }, { scale: 1, duration: 1.6, ease: 'expo.out', overwrite: true });
            }
          }
        });
        self.setActive(0);
        self.films(self.swiper);
      };
      if (window.Swiper) init(); else window.addEventListener('load', init, { once: true });

      this.thumbs.forEach(function (t) {
        t.addEventListener('click', function () { if (self.swiper) self.swiper.slideTo(parseInt(t.getAttribute('data-thumb'), 10)); });
      });
      if (this.prev) this.prev.addEventListener('click', function () { if (self.swiper) self.swiper.slidePrev(); });
      if (this.next) this.next.addEventListener('click', function () { if (self.swiper) self.swiper.slideNext(); });
      this.addEventListener('keydown', function (e) {
        if (!self.swiper || e.target.closest('input')) return;
        if (e.key === 'ArrowLeft') { e.preventDefault(); self.swiper.slidePrev(); }
        if (e.key === 'ArrowRight') { e.preventDefault(); self.swiper.slideNext(); }
      });

      this.scope.addEventListener('variant:change', function (e) {
        var v = e.detail && e.detail.variant;
        if (!v || !v.featured_media || !self.swiper) return;
        var slides = self.main.querySelectorAll('[data-media-id]');
        for (var i = 0; i < slides.length; i++) {
          // Index among all slides: a leading film slide has no media id.
          if (slides[i].getAttribute('data-media-id') === String(v.featured_media.id)) { self.swiper.slideTo(Array.prototype.indexOf.call(self.swiper.slides, slides[i])); break; }
        }
      });

      this.initZoom();
    }

    // The product film plays only while its slide is showing.
    films(s) {
      Array.prototype.forEach.call(s.slides, function (slide, n) {
        var v = slide.querySelector('video');
        if (!v) return;
        if (n === s.activeIndex) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
        else v.pause();
      });
    }

    setActive(i) {
      this.thumbs.forEach(function (t, n) { t.classList.toggle('is-active', n === i); t.setAttribute('aria-selected', n === i); });
      this.dots.forEach(function (dt, n) { dt.classList.toggle('is-active', n === i); });
      var total = this.swiper ? this.swiper.slides.length : 1;
      if (this.prev) { this.prev.disabled = i === 0; }
      if (this.next) { this.next.disabled = i >= total - 1; }
      if (this.now) {
        var label = String(i + 1).padStart(2, '0');
        var now = this.now;
        if (Theme.motionOn && window.gsap && now.textContent !== label) {
          gsap.timeline()
            .to(now, { yPercent: -110, duration: 0.35, ease: 'expo.in' })
            .call(function () { now.textContent = label; })
            .fromTo(now, { yPercent: 110 }, { yPercent: 0, duration: 0.7, ease: 'expo.out' });
        } else now.textContent = label;
      }
      if (this.bar) {
        var sx = (i + 1) / total;
        if (Theme.motionOn && window.gsap) gsap.to(this.bar, { scaleX: sx, duration: 1.1, ease: 'expo.out' });
        else this.bar.style.transform = 'scaleX(' + sx + ')';
      }
    }

    initZoom() {
      var overlay = this.scope.querySelector('[data-zoom-overlay]');
      if (!overlay) return;
      var img = overlay.querySelector('[data-zoom-img]');
      var stage = overlay.querySelector('[data-zoom-stage]');
      var close = function () {
        overlay.classList.remove('is-open');
        overlay.setAttribute('aria-hidden', 'true');
        if (Theme.unlockScroll) Theme.unlockScroll();
        if (Theme.releaseFocus) Theme.releaseFocus();
      };
      var pan = function (e) {
        if (!canHover || isMobile()) return;
        var r = stage.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        var ox = Math.max(0, img.offsetWidth - r.width) * px;
        var oy = Math.max(0, img.offsetHeight - r.height) * py;
        var t = 'translate(calc(-50% - ' + ox + 'px), calc(-50% - ' + oy + 'px))';
        if (window.gsap) gsap.to(img, { transform: t, duration: 1, ease: 'expo.out', overwrite: true });
        else img.style.transform = t;
      };
      this.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-zoom]');
        if (!trigger || (this.swiper && this.swiper.animating)) return;
        img.src = trigger.getAttribute('data-zoom');
        img.style.transform = 'translate(-50%, -50%)';
        overlay.classList.add('is-open');
        overlay.setAttribute('aria-hidden', 'false');
        if (Theme.lockScroll) Theme.lockScroll();
        if (Theme.trapFocus) Theme.trapFocus(overlay, overlay.querySelector('[data-zoom-close]'));
      }.bind(this));
      stage.addEventListener('mousemove', pan);
      stage.addEventListener('click', function () { if (canHover && !isMobile()) close(); });
      overlay.querySelector('[data-zoom-close]').addEventListener('click', close);
      overlay.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
    }
  }
  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

  /* ---------- Size guide popup ---------- */
  class SizeGuide extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.content = this.querySelector('.popup__content');
      var self = this;
      d.addEventListener('click', function (e) {
        if (e.target.closest('[data-size-guide-open]') && !e.target.closest('quick-add-popup')) { e.preventDefault(); self.open(); }
      });
      this.addEventListener('click', function (e) { if (e.target.closest('[data-popup-close]')) self.close(); });
      this.addEventListener('keydown', function (e) { if (e.key === 'Escape') self.close(); });
    }
    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      if (Theme.lockScroll) Theme.lockScroll();
      if (Theme.trapFocus) Theme.trapFocus(this.content);
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (Theme.unlockScroll) Theme.unlockScroll();
      if (Theme.releaseFocus) Theme.releaseFocus();
    }
  }
  if (!customElements.get('size-guide')) customElements.define('size-guide', SizeGuide);

  /* ---------- Sticky add to cart ---------- */
  function initSticky(scope) {
    var bar = scope.querySelector('[data-sticky-atc]');
    var atc = scope.querySelector('[data-atc]');
    if (!bar || !atc || !('IntersectionObserver' in window)) return;
    var footer = d.querySelector('#SiteFooter');
    // The bar steps aside while the info tabs (with their own tab bar) or the footer are on screen.
    var blockers = Array.prototype.slice.call(d.querySelectorAll('.pdp-tabs'));
    if (footer) blockers.push(footer);
    var inView = new Set();
    var atcHidden = false;
    // Once the shopper has scrolled past the tabs they're browsing (featured, recently viewed), not buying this
    // piece — the bar stays away there instead of sitting over the cards.
    var pastTabs = false;
    var tabs = blockers[0] && blockers[0].classList.contains('pdp-tabs') ? blockers[0] : null;
    var sync = function () {
      var show = atcHidden && inView.size === 0 && !pastTabs;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', !show);
      bar.querySelectorAll('button').forEach(function (b) { b.tabIndex = show ? 0 : -1; });
    };
    new IntersectionObserver(function (en) {
      atcHidden = !en[0].isIntersecting && en[0].boundingClientRect.top < 0;
      sync();
    }).observe(atc);
    var bio = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) inView.add(en.target); else inView.delete(en.target);
        if (en.target === tabs) pastTabs = !en.isIntersecting && en.boundingClientRect.top < 0;
      });
      sync();
    }, { rootMargin: '0px 0px -12% 0px' });
    blockers.forEach(function (b) { bio.observe(b); });

    // Mirror the chosen Length / Size so the shopper sees what the bar will add.
    var summary = bar.querySelector('[data-sticky-summary]');
    var props = scope.querySelectorAll('[data-property-option]');
    var renderSummary = function () {
      if (!props.length || !summary) return;
      var parts = [];
      props.forEach(function (fs) {
        var c = fs.querySelector('input:checked');
        var legend = fs.querySelector('.option__legend');
        if (c && legend) parts.push(legend.firstChild.textContent.trim() + ' ' + c.value);
      });
      summary.textContent = parts.join(' · ');
    };
    scope.addEventListener('change', renderSummary);
    renderSummary();

    scope.addEventListener('variant:change', function (e) {
      var v = e.detail.variant;
      var btn = bar.querySelector('button');
      var label = bar.querySelector('[data-atc-label]');
      if (btn) btn.disabled = !v.available;
      if (label) label.textContent = v.available ? (window.MirahSettings.strings.addToCart) : (window.MirahSettings.strings.soldOut);
    });
  }

  /* ---------- Tabs: sliding pill + Swiper panels on every screen (phones scroll the bar and can swipe panels) ---------- */
  class ProductTabs extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      var self = this;
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-tab]'));
      this.pill = this.querySelector('[data-tab-pill]');
      this.bar = this.querySelector('.pdp-tabs__bar');
      var el = this.querySelector('[data-tabs-slider]');
      var init = function () {
        if (!window.Swiper || !el) return;
        self.swiper = new Swiper(el, {
          slidesPerView: 1,
          spaceBetween: 20,
          speed: isMobile() ? 900 : 1200,
          allowTouchMove: isMobile(),
          resistanceRatio: 0.5,
          autoHeight: true,
          on: { slideChange: function (s) { self.select(s.activeIndex); } }
        });
        self.select(0, true);
        // autoHeight only measures on slide change; an FAQ opening inside a panel would grow past the clipped
        // wrapper (the last answers vanished). Follow the active panel's height as it changes.
        if ('ResizeObserver' in window) {
          var raf = 0;
          var ro = new ResizeObserver(function () {
            if (raf) return;
            raf = requestAnimationFrame(function () { raf = 0; if (self.swiper && !self.swiper.destroyed) self.swiper.updateAutoHeight(0); });
          });
          Array.prototype.forEach.call(self.swiper.slides, function (s) { ro.observe(s); });
        }
      };
      if (window.Swiper) init(); else window.addEventListener('load', init, { once: true });
      this.tabs.forEach(function (t, i) {
        t.addEventListener('click', function () { if (self.swiper) self.swiper.slideTo(i); });
        t.addEventListener('keydown', function (e) {
          var dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!dir) return;
          e.preventDefault();
          var n = (i + dir + self.tabs.length) % self.tabs.length;
          self.tabs[n].focus();
          if (self.swiper) self.swiper.slideTo(n);
        });
      });
      window.addEventListener('resize', Theme.debounce ? Theme.debounce(function () { self.movePill(true); self.edges(); }, 150) : function () {});

      // Overflow cues (phones): edge arrows step to the neighbouring tab; the fades show only where there's more.
      this.wrap = this.querySelector('[data-tabs-wrap]');
      this.now = this.querySelector('[data-tabs-now]');
      Array.prototype.forEach.call(this.querySelectorAll('[data-tabs-step]'), function (btn) {
        btn.addEventListener('click', function () {
          var n = Math.max(0, Math.min(self.tabs.length - 1, (self.current || 0) + parseInt(btn.getAttribute('data-tabs-step'), 10)));
          if (self.swiper) self.swiper.slideTo(n);
        });
      });
      if (this.bar) this.bar.addEventListener('scroll', function () { self.edges(); }, { passive: true });
      this.edges();
      // First time the bar comes into view with hidden tabs, it nudges sideways to show it scrolls.
      if ('IntersectionObserver' in window && this.bar) {
        var io = new IntersectionObserver(function (en) {
          if (!en[0].isIntersecting) return;
          io.disconnect();
          if (self.bar.scrollWidth <= self.bar.clientWidth + 2 || self.bar.scrollLeft > 4) return;
          setTimeout(function () {
            self.bar.scrollTo({ left: 70, behavior: 'smooth' });
            setTimeout(function () { self.bar.scrollTo({ left: 0, behavior: 'smooth' }); }, 700);
          }, 500);
        }, { threshold: 0.9 });
        io.observe(this.bar);
      }
    }
    edges() {
      if (!this.wrap || !this.bar) return;
      var max = this.bar.scrollWidth - this.bar.clientWidth;
      var over = max > 2;
      var was = this.wrap.classList.contains('is-scrollable');
      this.wrap.classList.toggle('is-scrollable', over);
      // Fitting tabs drop the arrow gutter (CSS), which moves them: re-seat the pill.
      if (was !== over && this.movePill) { var self = this; requestAnimationFrame(function () { self.movePill(true); }); }
      this.wrap.classList.toggle('has-prev', over && this.bar.scrollLeft > 4);
      this.wrap.classList.toggle('has-next', over && this.bar.scrollLeft < max - 4);
      if (this.now) this.now.textContent = (this.current || 0) + 1;
    }
    select(i, instant) {
      this.current = i;
      this.tabs.forEach(function (t, n) {
        t.setAttribute('aria-selected', n === i);
        t.tabIndex = n === i ? 0 : -1;
      });
      this.movePill(instant);
      if (this.edges) this.edges();
      // The incoming panel's image settles and its text rises line by line as the slider glides over.
      if (!instant && Theme.motionOn && window.gsap && this.swiper) {
        var panel = this.swiper.slides[i];
        var media = panel && panel.querySelector('.pdp-tabs__media img');
        var bits = panel ? panel.querySelectorAll('.pdp-tabs__text > *, .pdp-tabs__faq .faq') : [];
        if (media) gsap.fromTo(media, { scale: 1.18 }, { scale: 1, duration: 1.8, ease: 'expo.out' });
        if (bits.length) gsap.fromTo(bits, { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 1.1, ease: 'expo.out', stagger: 0.06, delay: 0.25 });
      }
      if (window.ScrollTrigger) setTimeout(function () { ScrollTrigger.refresh(); }, 1250);
    }
    movePill(instant) {
      var tab = this.tabs[this.current || 0];
      if (!tab || !this.pill) return;
      var li = tab.parentElement;
      var vars = { x: li.offsetLeft, width: li.offsetWidth };
      // Phones: keep the active tab centred in the scrolling bar.
      if (this.bar && this.bar.scrollWidth > this.bar.clientWidth + 2) {
        this.bar.scrollTo({ left: li.offsetLeft - (this.bar.clientWidth - li.offsetWidth) / 2, behavior: instant ? 'auto' : 'smooth' });
      }
      if (instant || !window.gsap) { if (window.gsap) gsap.set(this.pill, vars); else { this.pill.style.width = vars.width + 'px'; this.pill.style.transform = 'translateX(' + vars.x + 'px)'; } return; }
      gsap.to(this.pill, Object.assign({ ease: 'expo.out', duration: 1.2 }, vars));
    }
  }
  if (!customElements.get('product-tabs')) customElements.define('product-tabs', ProductTabs);

  /* ---------- Recommendations (Search & Discovery) ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      var self = this;
      var load = function () {
        fetch(self.getAttribute('data-url'))
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var fresh = doc.querySelector('product-recommendations');
            if (fresh && fresh.querySelector('[data-product-card]')) {
              self.innerHTML = fresh.innerHTML;
              if (Theme.initMotion) Theme.initMotion(self);
              if (window.MirahRails) window.MirahRails.init(self);
            } else {
              self.closest('.shopify-section').hidden = true;
            }
          })
          .catch(function () {});
      };
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (en) { if (en[0].isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '0px 0px 400px 0px' });
        io.observe(this);
      } else load();
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Recently viewed ---------- */
  var KEY = 'mirah:recent';
  function remember(handle) {
    try {
      var list = JSON.parse(localStorage.getItem(KEY) || '[]').filter(function (h) { return h !== handle; });
      list.unshift(handle);
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, 12)));
    } catch (e) {}
  }
  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      var current = this.getAttribute('data-current');
      var limit = parseInt(this.getAttribute('data-limit'), 10) || 4;
      var list = [];
      try { list = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) {}
      list = list.filter(function (h) { return h !== current; }).slice(0, limit);
      var section = this.closest('.shopify-section');
      if (!list.length) { if (section) section.hidden = true; return; }
      var grid = this.querySelector('[data-recent-grid]');
      var self = this;
      Promise.all(list.map(function (h) {
        return fetch('/products/' + encodeURIComponent(h) + '?section_id=product-card-fragment')
          .then(function (r) { return r.ok ? r.text() : ''; })
          .catch(function () { return ''; });
      })).then(function (parts) {
        var html = parts.map(function (p) {
          var doc = new DOMParser().parseFromString(p, 'text/html');
          var card = doc.querySelector('[data-product-card]');
          return card ? '<div data-stagger-item>' + card.outerHTML + '</div>' : '';
        }).join('');
        if (!html) { if (section) section.hidden = true; return; }
        grid.innerHTML = html;
        if (Theme.iconClone) Theme.iconClone(self);
        if (Theme.motionOn && window.gsap) gsap.fromTo(grid.children, { y: 40, opacity: 0 }, { y: 0, opacity: 1, stagger: 0.1, duration: 1.2, ease: 'customSlow' });
      });
    }
  }
  if (!customElements.get('recently-viewed')) customElements.define('recently-viewed', RecentlyViewed);

  /* ---------- Boot per product section ---------- */
  function boot(root) {
    (root || d).querySelectorAll('[data-product-scope]').forEach(function (scope) {
      if (scope._booted) return;
      scope._booted = true;
      initSticky(scope);
      var handle = scope.getAttribute('data-product-handle');
      if (handle) remember(handle);
    });
  }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  d.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
